/**
 * ImpersonationRedirect - rising/falling-edge navigator on impersonating_as.
 *
 * Phase 3.1 Plan 03 (B1 mitigation, per ADR-006 success criteria 4 + 5):
 *   Mounted unconditionally inside AppShell so it observes EVERY user
 *   transition for any authenticated route. Watches the previous and
 *   current user.impersonating_as values via useRef and navigates on
 *   edges:
 *
 *     - false -> true: useNavigate('/now', { replace: true })
 *       The token swap just made admin routes unavailable; if the user was
 *       on /admin/fleet they would otherwise hit the SPA 404. AppShell
 *       stays mounted on the rising edge (the route subtree rebuilds, the
 *       layout does not), so useNavigate commits cleanly.
 *
 *     - true -> false: window.location.assign('/admin/fleet')  [Phase 4]
 *       Phase 3.1 used useNavigate here too, but the AppRouter rebuilds
 *       the entire createBrowserRouter on the falling edge (claim flip
 *       changes the route surface). The about-to-unmount AppShell
 *       fires useNavigate inside an unmounting tree and the navigation
 *       does not always commit before the new router takes over,
 *       leaving the user at the catch-all 404. The Phase 3.1 Playwright
 *       E2E worked around this with an explicit page.goto.
 *
 *       Phase 4 fix: hard-nav via window.location.assign forces a full
 *       page reload that bypasses the router rebuild race entirely.
 *       Cost: full page reload (~150-300ms; acceptable, exit happens
 *       once per session). Benefit: deterministic commitment of the
 *       navigation across the AppRouter rebuild. The new /me bootstrap
 *       on /admin/fleet sees the staff token in active slots and the
 *       sidecar cleared, so the staff routes are correctly registered.
 *
 *     - First render: initialize the prev ref to the current value; do
 *       not navigate. This avoids a redirect loop when the SPA boots into
 *       an already-impersonating session.
 *
 *     - Steady state (no transition) and client_user (always
 *       impersonating_as=false): no-op.
 *
 *   Renders nothing; side-effect-only via useEffect.
 *
 *   StrictMode safety: the first-render initialization guard handles the
 *   double-mount cleanly; navigation only fires on actual state-change
 *   rerenders.
 *
 *   Test surface: the falling-edge hard-nav uses window.location.assign,
 *   which is mockable via vi.spyOn(window.location, 'assign'). The
 *   useNavigate path on the rising edge stays as the testable react-
 *   router-dom mock pattern from Phase 3.1.
 *
 * Modified: 2026-04-26 (Phase 4 Plan 04)
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Side-effect-only component. Returns null but watches user transitions
 * via useEffect and navigates on the rising / falling edge of
 * user.impersonating_as.
 *
 * Falling edge uses a hard navigation (window.location.assign) to avoid
 * the AppRouter rebuild race documented in Phase 3.1 Plan 04 SUMMARY.
 */
export function ImpersonationRedirect(): null {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  // undefined sentinel for "first render not yet seen"; once initialized
  // this holds the previous boolean value of user.impersonating_as.
  const prevImpersonatingRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    // Reset the ref while AuthProvider is initializing or the user is
    // logged out. The next authenticated render will re-initialize.
    if (status !== 'authenticated' || !user) {
      prevImpersonatingRef.current = undefined;
      return;
    }

    const current = !!user.impersonating_as;
    const prev = prevImpersonatingRef.current;

    // Initialize on first render without firing a redirect; the user is
    // already on a route compatible with their current impersonating_as
    // value (either via fresh login or via app boot into an existing
    // session).
    if (prev === undefined) {
      prevImpersonatingRef.current = current;
      return;
    }

    if (prev === false && current === true) {
      // Rising edge: token swap completed; navigate into the client
      // surface. /now is the canonical client landing per ADR-006.
      // AppShell stays mounted across this transition (only the route
      // subtree rebuilds), so useNavigate commits cleanly.
      navigate('/now', { replace: true });
    } else if (prev === true && current === false) {
      // Falling edge: exit completed. The AppRouter rebuilds the entire
      // createBrowserRouter (claim flip), which unmounts AppShell mid-
      // navigation. Phase 3.1 useNavigate did not always commit before
      // the new router took over (Phase 3.1 Plan 04 SUMMARY documented
      // this race + Playwright workaround). Phase 4 fix: hard-nav via
      // window.location.assign forces a full page reload that bypasses
      // the router rebuild race; the user lands deterministically on
      // /admin/fleet with a fresh /me bootstrap.
      window.location.assign('/admin/fleet');
    }

    // Remember the new value for the next transition.
    prevImpersonatingRef.current = current;
  }, [user, status, navigate]);

  return null;
}
